import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toPng, toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Loader2, Download, X, BarChart3, Network, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import TrafficLights from './TrafficLights';
import CompareAll from './CompareAll';
import PhotographerReport from './PhotographerReport';
import GoldPerformer from './GoldPerformer';
import './TeamStructure.css';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_META = [
  { key: 'director', label: 'Director' },
  { key: 'area_manager', label: 'Area Managers' },
  { key: 'support', label: 'Support Team' },
  { key: 'photographer', label: 'Photographers' },
];

// Per-area label overrides to match each guide diagram exactly
const AREA_GROUP_LABELS = {
  all: { director: 'Directors', support: 'Head Office' },
  southern: { director: 'Regional Director', support: 'Client Support' },
  northern: { director: 'Regional Director', support: 'Client Support' },
  rsa: { support: 'South Africa Team' },
  ho: { director: 'Chairman and Founder', area_manager: 'Executive Team', support: 'Head Office Team' },
};

// Groups that don't exist in certain areas — hidden from the chart entirely.
// South Africa runs as ONE team for now (no director / AMs / photographers).
const AREA_HIDDEN_GROUPS = {
  ho: ['photographer'],
  rsa: ['director', 'area_manager', 'photographer'],
};

const AREA_SHORT = { orbital: 'Orbital', southern: 'Southern', northern: 'Northern', rsa: 'S. Africa', ho: 'Head Office' };

// Leadership pulled out of the normal groups on the All tab — they sit above the directors.
const ALL_LEADERSHIP = { 'ho-andy-south': 'founder', 'ho-peter-burnham': 'md' };

const groupLabel = (area, groupKey) =>
  AREA_GROUP_LABELS[area]?.[groupKey] || GROUP_META.find((g) => g.key === groupKey)?.label;

function MemberCard({ m, stat, canEdit, onDragStart, onClick }) {
  return (
    <div
      className={`ts-member ${canEdit ? '' : 'ts-static'}`}
      draggable={canEdit}
      onDragStart={(e) => canEdit && onDragStart(e, m)}
      onClick={() => onClick(m)}
      title={m.name}
      data-testid={`ts-member-${m.id}`}
    >
      <div className={`ts-photo ${stat ? `ts-ring-${stat.band}` : ''}`}>
        <img src={`${API}${m.photo}`} alt={m.name} loading="lazy" draggable={false} />
        {stat && <span className={`ts-grade-badge ts-badge-${stat.band}`}>{stat.grade.split('>').pop().trim()}</span>}
      </div>
      <span className="ts-pill">{m.first_name}</span>
      {m.covering && <span className="ts-covering" data-testid={`ts-covering-${m.id}`}>{m.covering}</span>}
      {m.area_tag && <span className="ts-area-mini">{m.area_tag}</span>}
    </div>
  );
}

function GroupSection({ group, label, members, statsMap, canEdit, onDragStart, onDragEnd, onDrop, onClickMember, wide }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`ts-group ts-group-${group} ${members.length === 1 ? 'ts-solo' : ''} ${wide ? 'ts-group-wide' : ''} ${over ? 'is-over' : ''} ${members.length === 0 ? 'is-empty' : ''}`}
      onDragOver={(e) => { if (canEdit) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { if (canEdit) { e.preventDefault(); setOver(false); onDrop(e, group); } }}
      data-testid={`ts-group-${group}`}
    >
      <div className="ts-bracket">
        <span className="ts-group-label">{label}</span>
      </div>
      <div className="ts-group-body">
        {members.map((m) => (
          <MemberCard key={m.id} m={m} stat={statsMap[m.id]} canEdit={canEdit} onDragStart={onDragStart} onClick={onClickMember} />
        ))}
        {members.length === 0 && <div className="ts-empty-slot">{canEdit ? 'Drop here' : '—'}</div>}
      </div>
    </div>
  );
}

export default function TeamStructure({ fullscreen = false }) {
  const { user } = useAuth();
  const canEdit = ['super_admin', 'md'].includes(user?.role);
  const canSeeStats = canEdit;

  const [areas, setAreas] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeArea, setActiveArea] = useState('all');
  const [view, setView] = useState('chart');
  const [statsMap, setStatsMap] = useState({});
  const [statsAreas, setStatsAreas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cardMemberId, setCardMemberId] = useState(null);
  const [goldMember, setGoldMember] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const diagramRef = useRef(null);
  const dragId = useRef(null);
  const hoverSwitch = useRef({ key: null, timer: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/staff/structure`);
        setAreas(r.data.areas);
        setMembers(r.data.members);
        setStatsMap(r.data.stats_map || {});
        setStatsAreas(r.data.stats_areas || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load team structure');
      }
      setLoading(false);
    })();
  }, []);

  const onDragStart = (e, m) => {
    dragId.current = m.id;
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
  };

  const cancelAreaSwitch = useCallback(() => {
    if (hoverSwitch.current.timer) clearTimeout(hoverSwitch.current.timer);
    hoverSwitch.current = { key: null, timer: null };
  }, []);

  const onDragEnd = useCallback(() => {
    setDragging(false);
    cancelAreaSwitch();
  }, [cancelAreaSwitch]);

  // Safety net: source card can unmount mid-drag after a spring-loaded area
  // switch, so its dragend never bubbles — clear on the next real mouse event.
  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('drop', onDragEnd);
    window.addEventListener('mousemove', onDragEnd);
    return () => {
      window.removeEventListener('drop', onDragEnd);
      window.removeEventListener('mousemove', onDragEnd);
    };
  }, [dragging, onDragEnd]);

  const scheduleAreaSwitch = (key) => {
    if (key === activeArea) { cancelAreaSwitch(); return; }
    if (hoverSwitch.current.key === key) return;
    cancelAreaSwitch();
    hoverSwitch.current = {
      key,
      timer: setTimeout(() => {
        hoverSwitch.current = { key: null, timer: null };
        setActiveArea(key);
        setView('chart');
      }, 400),
    };
  };

  const persistMove = async (id, patch) => {
    try {
      await axios.put(`${API}/api/staff/member/${id}`, patch);
    } catch (e) {
      toast.error('Move failed to save');
    }
  };

  const onDropGroup = (e, group) => {
    const id = dragId.current;
    if (!id) return;
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, group, area: activeArea, sort_order: 999 } : m)));
    persistMove(id, { group, area: activeArea, sort_order: 999 });
    dragId.current = null;
  };

  const onDropArea = (areaKey) => {
    const id = dragId.current;
    if (!id) return;
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, area: areaKey, sort_order: 999 } : m)));
    persistMove(id, { area: areaKey, sort_order: 999 });
    toast.success(`Moved to ${areas.find((a) => a.key === areaKey)?.label}`);
    dragId.current = null;
  };

  const handleMemberClick = (m) => {
    if (canSeeStats && m.id === 'ho-jon-hawkins') { setGoldMember(m); return; }
    if (canSeeStats && statsMap[m.id]) setCardMemberId(m.id);
    else setSelected(m);
  };

  const isAllView = activeArea === 'all';
  const canDrag = canEdit && !isAllView;
  const areaMembers = isAllView
    ? members.filter((m) => !m.id.startsWith('ho2-'))
    : members.filter((m) => m.area === activeArea);
  const byGroup = (g) => {
    const list = areaMembers.filter((m) => m.group === g);
    if (isAllView) {
      return list
        .filter((m) => !ALL_LEADERSHIP[m.id])
        .sort((a, b) => a.first_name.localeCompare(b.first_name) || a.name.localeCompare(b.name))
        .map((m) => ({ ...m, area_tag: AREA_SHORT[m.area] || m.area }));
    }
    return list.sort((a, b) => (a.sort_order - b.sort_order) || a.first_name.localeCompare(b.first_name));
  };
  const leadership = (role) => areaMembers.filter((m) => ALL_LEADERSHIP[m.id] === role);

  const areaLabel = isAllView ? 'All-Teams' : (areas.find((a) => a.key === activeArea)?.label || '');
  const diagramTitle = isAllView ? 'NicheCom — All Teams' : `${areaLabel} Team`;
  const hiddenGroups = AREA_HIDDEN_GROUPS[activeArea] || [];
  const hasReport = canSeeStats && statsAreas.includes(activeArea);

  const doExport = useCallback(async (format) => {
    if (!diagramRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      const node = diagramRef.current;
      // Freeze the layout: pin every member card at its exact on-screen position so
      // the export clone cannot re-wrap anything (html-to-image re-layouts flex-wrap).
      const nodeRect = node.getBoundingClientRect();
      const restores = [];
      const freeze = (el, extra = {}) => {
        const r = el.getBoundingClientRect();
        const prev = el.getAttribute('style') || '';
        restores.push(() => el.setAttribute('style', prev));
        Object.assign(el.style, extra, { width: `${r.width}px`, height: `${r.height}px` });
        return r;
      };
      node.querySelectorAll('.ts-group-body').forEach((body) => {
        const bodyRect = body.getBoundingClientRect();
        const cards = [...body.children];
        const positions = cards.map((c) => c.getBoundingClientRect());
        freeze(body, { position: 'relative' });
        cards.forEach((c, i) => {
          const r = positions[i];
          freeze(c, { position: 'absolute', left: `${r.left - bodyRect.left}px`, top: `${r.top - bodyRect.top}px`, margin: '0' });
        });
      });
      freeze(node);
      void nodeRect;
      const opts = { backgroundColor: '#ffffff', pixelRatio: 2, includeQueryParams: true };
      try {
        if (format === 'png' || format === 'jpg') {
          const dataUrl = format === 'png' ? await toPng(node, opts) : await toJpeg(node, { ...opts, quality: 0.92 });
          const a = document.createElement('a');
          a.download = `${areaLabel.replace(/\s+/g, '-')}-Team.${format}`;
          a.href = dataUrl;
          a.click();
        } else {
          const dataUrl = await toPng(node, opts);
          const img = new Image();
          await new Promise((res) => { img.onload = res; img.src = dataUrl; });
          const pdf = new jsPDF({ orientation: img.width >= img.height ? 'landscape' : 'portrait', unit: 'px', format: [img.width / 2, img.height / 2] });
          pdf.addImage(dataUrl, 'PNG', 0, 0, img.width / 2, img.height / 2);
          pdf.save(`${areaLabel.replace(/\s+/g, '-')}-Team.pdf`);
        }
      } finally {
        restores.forEach((fn) => fn());
      }
      toast.success(`${areaLabel} team downloaded as ${format.toUpperCase()}`);
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    }
    setExporting(false);
  }, [areaLabel]);

  if (loading) {
    return <div className="ts-loading"><Loader2 size={22} className="ts-spin" /> Loading team structure…</div>;
  }

  return (
    <div className={`ts-wrap ${fullscreen ? 'ts-fullscreen' : ''}`} data-testid="team-structure">
      <div className="ts-toolbar">
        <div className={`ts-area-tabs ${dragging ? 'is-dragging' : ''}`}>
          {[{ key: 'all', label: 'All' }, ...areas].map((a) => (
            <button
              key={a.key}
              className={`ts-area-tab ${activeArea === a.key ? 'is-active' : ''}`}
              onClick={() => { setActiveArea(a.key); setView('chart'); }}
              onDragOver={(e) => { if (canEdit && a.key !== 'all') { e.preventDefault(); scheduleAreaSwitch(a.key); } }}
              onDragLeave={() => { if (hoverSwitch.current.key === a.key) cancelAreaSwitch(); }}
              onDrop={(e) => { if (canEdit && a.key !== 'all') { e.preventDefault(); cancelAreaSwitch(); onDropArea(a.key); } }}
              data-testid={`ts-area-${a.key}`}
            >
              {a.label}
              <span className="ts-count">
                {a.key === 'all'
                  ? members.filter((m) => !m.id.startsWith('ho2-')).length
                  : members.filter((m) => m.area === a.key).length}
              </span>
            </button>
          ))}
        </div>
        <div className="ts-downloads">
          {canSeeStats && (
            <div className="ts-view-toggle" data-testid="ts-view-toggle">
              <button className={view === 'chart' ? 'is-active' : ''} onClick={() => setView('chart')} data-testid="ts-view-chart">
                <Network size={13} /> Org Chart
              </button>
              {hasReport && (
                <button className={view === 'traffic' ? 'is-active' : ''} onClick={() => setView('traffic')} data-testid="ts-view-traffic">
                  <BarChart3 size={13} /> Traffic Lights
                </button>
              )}
              <button className={view === 'compare' ? 'is-active' : ''} onClick={() => setView('compare')} data-testid="ts-view-compare">
                <Trophy size={13} /> Compare All
              </button>
            </div>
          )}
          {view === 'chart' && ['png', 'jpg', 'pdf'].map((f) => (
            <button key={f} className="ts-dl-btn" onClick={() => doExport(f)} disabled={exporting} data-testid={`ts-download-${f}`}>
              {exporting ? <Loader2 size={13} className="ts-spin" /> : <Download size={13} />}
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {view === 'traffic' ? (
        <TrafficLights area={activeArea} onOpenCard={setCardMemberId} />
      ) : view === 'compare' ? (
        <CompareAll onOpenReport={setCardMemberId} />
      ) : (
        <>
          <p className="ts-hint">
            {isAllView
              ? 'Company-wide view — everyone across every area, grouped by role. Switch to a region tab to drag and drop. Click a person for their profile.'
              : canEdit
              ? 'Drag a person onto a group to change their role. To move areas: hold them over an area tab — it opens so you can drop them into the right group — or drop straight on the tab. Click a person for their profile.'
              : 'Click a person to see their profile.'}
            {hasReport && ' Coloured rings show this quarter\u2019s traffic light standing; the badge is their grade (1\u20139).'}
          </p>
          <div className={`ts-diagram ${exporting ? 'is-exporting' : ''}`} ref={diagramRef}>
            <div className="ts-diagram-title">{diagramTitle}</div>
            {isAllView && (
              <div className="ts-top-row ts-leadership-row">
                <GroupSection group="founder" label="Founder" members={leadership('founder')} statsMap={statsMap} canEdit={false} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} />
                <GroupSection group="md" label="Managing Director" members={leadership('md')} statsMap={statsMap} canEdit={false} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} />
                <GroupSection group="director" label={groupLabel(activeArea, 'director')} members={byGroup('director')} statsMap={statsMap} canEdit={false} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} />
              </div>
            )}
            <div className="ts-top-row">
              {!hiddenGroups.includes('director') && !isAllView && (
                <GroupSection group="director" label={groupLabel(activeArea, 'director')} members={byGroup('director')} statsMap={statsMap} canEdit={canDrag} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} />
              )}
              {!hiddenGroups.includes('area_manager') && (
                <GroupSection group="area_manager" label={groupLabel(activeArea, 'area_manager')} members={byGroup('area_manager')} statsMap={statsMap} canEdit={canDrag} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} />
              )}
              {!hiddenGroups.includes('support') && (
                <GroupSection group="support" label={groupLabel(activeArea, 'support')} members={isAllView ? byGroup('support').filter((m) => m.area !== 'rsa') : byGroup('support')} statsMap={statsMap} canEdit={canDrag} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} wide={hiddenGroups.includes('director') && hiddenGroups.includes('area_manager')} />
              )}
            </div>
            {!hiddenGroups.includes('photographer') && (
              <GroupSection group="photographer" label={groupLabel(activeArea, 'photographer')} members={byGroup('photographer')} statsMap={statsMap} canEdit={canDrag} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} wide />
            )}
            {isAllView && (
              <GroupSection group="rsa_team" label="South Africa" members={byGroup('support').filter((m) => m.area === 'rsa').map((m) => ({ ...m, area_tag: null }))} statsMap={statsMap} canEdit={false} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDropGroup} onClickMember={handleMemberClick} wide />
            )}
          </div>
        </>
      )}

      {goldMember && (
        <GoldPerformer member={goldMember} onClose={() => setGoldMember(null)} />
      )}

      {cardMemberId && (
        <PhotographerReport memberId={cardMemberId} onClose={() => setCardMemberId(null)} />
      )}

      {selected && createPortal(
        <div className="ts-modal-overlay" onClick={() => setSelected(null)} data-testid="ts-member-modal">
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ts-modal-close" onClick={() => setSelected(null)} data-testid="ts-modal-close"><X size={16} /></button>
            <div className="ts-modal-photo"><img src={`${API}${selected.photo}`} alt={selected.name} /></div>
            <h3>{selected.name}</h3>
            <p className="ts-modal-role">
              {groupLabel(selected.area, selected.group)} · {areas.find((a) => a.key === selected.area)?.label}
            </p>
            <div className="ts-modal-stats">
              <BarChart3 size={16} />
              <span>Performance stats coming soon</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
